import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { AcaoDetalheDto, AcaoFundamentosDto, companyService } from './service/company.service';
import { formatCurrency } from '../../../utils/formatCurrency';

const CHART_RANGE_OPTIONS = ['1d', '5d', '1mo', '3mo', '6mo', '1y', '5y', '10y'] as const;
type ChartRangeOption = (typeof CHART_RANGE_OPTIONS)[number];

type QuoteSummary = {
  symbol: string;
  shortName: string | null;
  longName: string | null;
  currency: string;
  regularMarketPrice: number | null;
  regularMarketChange: number | null;
  regularMarketChangePercent: number | null;
  marketCap: number | null;
  regularMarketVolume: number | null;
  priceEarnings: number | null;
  earningsPerShare: number | null;
  fiftyTwoWeekRange: string | null;
  logourl: string | null;
};

const parseChartDate = (value: string | number | null | undefined): Date | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') {
    const normalized = value < 1_000_000_000_000 ? value * 1000 : value;
    const parsedNumber = new Date(normalized);
    return Number.isNaN(parsedNumber.getTime()) ? null : parsedNumber;
  }

  const trimmed = value.trim();
  if (!trimmed) return null;

  if (/^\d+$/.test(trimmed)) {
    const asNumber = Number(trimmed);
    const normalized = asNumber < 1_000_000_000_000 ? asNumber * 1000 : asNumber;
    const parsedNumeric = new Date(normalized);
    if (!Number.isNaN(parsedNumeric.getTime())) {
      return parsedNumeric;
    }
  }

  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatPrice = (value: number | null | undefined, currency = 'BRL'): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return 'N/D';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(value);
};

const formatDecimal = (value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return 'N/D';
  return value.toFixed(2).replace('.', ',');
};

const formatInteger = (value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return 'N/D';
  return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(value);
};

const formatVariation = (value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return 'N/D';
  return `${value.toFixed(2).replace('.', ',')}%`;
};

const formatAxisLabelByRange = (date: Date, range: ChartRangeOption): string => {
  if (range === '1d') {
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
};

const extractTickersFromCompany = (company: { ticker?: string; tickers?: string[] | null } | undefined): string[] => {
  const tickers = [company?.ticker, ...(company?.tickers || [])]
    .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
    .map((item) => item.trim().toUpperCase());
  return Array.from(new Set(tickers));
};

const extractQuoteSummary = (
  fundamentals: AcaoFundamentosDto | null,
  priceData: AcaoDetalheDto | null
): QuoteSummary | null => {
  const resultItem = fundamentals?.results?.[0];

  if (resultItem) {
    return {
      symbol: (resultItem.symbol || '').toUpperCase(),
      shortName: resultItem.shortName || null,
      longName: resultItem.longName || null,
      currency: resultItem.currency || 'BRL',
      regularMarketPrice: resultItem.regularMarketPrice ?? null,
      regularMarketChange: resultItem.regularMarketChange ?? null,
      regularMarketChangePercent: resultItem.regularMarketChangePercent ?? null,
      marketCap: resultItem.marketCap ?? null,
      regularMarketVolume: resultItem.regularMarketVolume ?? null,
      priceEarnings: resultItem.priceEarnings ?? null,
      earningsPerShare: resultItem.earningsPerShare ?? null,
      fiftyTwoWeekRange: resultItem.fiftyTwoWeekRange ?? null,
      logourl: resultItem.logourl ?? null,
    };
  }

  const data = priceData;
  if (!data) return null;

  return {
    symbol: (data.ticker || '').toUpperCase(),
    shortName: data.ticker || null,
    longName: data.nome || null,
    currency: data.moeda || 'BRL',
    regularMarketPrice: data.precoAtual ?? null,
    regularMarketChange: data.variacao ?? null,
    regularMarketChangePercent: data.variacaoPercentual ?? null,
    marketCap: data.valorDeMercado ?? null,
    regularMarketVolume: data.volume ?? null,
    priceEarnings: data.precoLucro ?? null,
    earningsPerShare: data.lucroPorAcao ?? null,
    fiftyTwoWeekRange: null,
    logourl: data.logoUrl ?? null,
  };
};

const CompanyDetailsPage: React.FC = () => {
  const { ticker } = useParams<{ ticker: string }>();
  const navigate = useNavigate();
  const routeTicker = useMemo(() => decodeURIComponent(ticker || '').trim().toUpperCase(), [ticker]);

  const [selectedTicker, setSelectedTicker] = useState(routeTicker);
  const [companyData, setCompanyData] = useState<AcaoDetalheDto | null>(null);
  const [fundamentalsData, setFundamentalsData] = useState<AcaoFundamentosDto | null>(null);
  const [availableTickers, setAvailableTickers] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [isPriceChartLoading, setIsPriceChartLoading] = useState(false);
  const [selectedChartRange, setSelectedChartRange] = useState<ChartRangeOption>('1mo');

  useEffect(() => {
    setSelectedTicker(routeTicker);
  }, [routeTicker]);

  useEffect(() => {
    const loadCompanyTickers = async () => {
      if (!selectedTicker) return;
      const allCompanies = await companyService.getAllCompanies();
      const matchedCompany = allCompanies.find((item) => extractTickersFromCompany(item).includes(selectedTicker));
      const related = extractTickersFromCompany(matchedCompany);
      setAvailableTickers(related.length > 0 ? related : [selectedTicker]);
    };

    loadCompanyTickers();
  }, [selectedTicker]);

  useEffect(() => {
    const loadFundamentals = async () => {
      if (!selectedTicker) {
        setFundamentalsData(null);
        return;
      }

      const data = await companyService.getFundamentalsByTicker(selectedTicker);
      setFundamentalsData(data);
    };

    loadFundamentals();
  }, [selectedTicker]);

  useEffect(() => {
    const loadTickerData = async () => {
      if (!selectedTicker) {
        setLoading(false);
        return;
      }

      try {
        if (!companyData) setLoading(true);
        setIsPriceChartLoading(true);
        const data = await companyService.getTickerPriceData(selectedTicker, selectedChartRange);
        setCompanyData(data);
      } finally {
        setLoading(false);
        setIsPriceChartLoading(false);
      }
    };

    loadTickerData();
  }, [selectedTicker, selectedChartRange]);

  const quote = useMemo(
    () => extractQuoteSummary(fundamentalsData, companyData),
    [fundamentalsData, companyData]
  );
  const variationPercent = quote?.regularMarketChangePercent ?? null;
  const isPositiveVariation = (variationPercent ?? 0) >= 0;

  const priceHistory = useMemo(
    () =>
      [...(companyData?.historicalDataPrice || [])]
        .filter((item) => typeof item.fechamento === 'number')
        .sort((a, b) => {
          const aDate = parseChartDate(a.data)?.getTime() || 0;
          const bDate = parseChartDate(b.data)?.getTime() || 0;
          return aDate - bDate;
        })
        .map((item) => ({
          dateRaw: item.data,
          dateLabel: (() => {
            const parsed = parseChartDate(item.data);
            if (!parsed) return String(item.data);
            return formatAxisLabelByRange(parsed, selectedChartRange);
          })(),
          fechamento: item.fechamento,
        })),
    [companyData?.historicalDataPrice, selectedChartRange]
  );

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen-1/2 gap-4">
        <div className="w-12 h-12 border-4 border-gray-200 border-t-blue-700 rounded-full animate-spin"></div>
        <p>Carregando detalhes da empresa...</p>
      </div>
    );
  }

  if (!companyData || !quote) {
    return (
      <div className="p-4">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Empresa não encontrada</h1>
          <p className="text-gray-600 mb-4">Não foi possível carregar os dados da empresa selecionada.</p>
          <button
            type="button"
            onClick={() => navigate('/empresas')}
            className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            Voltar para empresas
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <div className="mb-6">
        <button
          type="button"
          onClick={() => navigate('/empresas')}
          className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50"
        >
          Voltar para empresas
        </button>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-6 md:p-8 shadow-sm mb-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-4">
            {quote.logourl ? (
              <img
                src={quote.logourl}
                alt={`Logo ${quote.longName || quote.symbol}`}
                className="w-16 h-16 rounded-full border border-gray-200 bg-white p-1"
              />
            ) : (
              <div className="w-16 h-16 rounded-full border border-gray-200 bg-gray-50" />
            )}
            <div>
              <p className="text-sm font-semibold text-blue-700">{quote.symbol || selectedTicker}</p>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                {quote.longName || quote.shortName || 'Empresa'}
              </h1>
              {availableTickers.length > 1 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {availableTickers.map((tickerOption) => (
                    <button
                      key={tickerOption}
                      type="button"
                      onClick={() => {
                        setSelectedChartRange('1mo');
                        navigate(`/empresas/${encodeURIComponent(tickerOption)}`);
                      }}
                      className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                        tickerOption === selectedTicker
                          ? 'bg-blue-700 border-blue-700 text-white'
                          : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {tickerOption}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="text-left md:text-right">
            <p className="text-sm text-gray-500 mb-1">Preço atual</p>
            <p className="text-3xl font-extrabold text-gray-900">{formatPrice(quote.regularMarketPrice, quote.currency)}</p>
            <div
              className={`mt-1 inline-flex items-center gap-1 text-sm font-semibold ${
                isPositiveVariation ? 'text-emerald-600' : 'text-red-600'
              }`}
            >
              {isPositiveVariation ? (
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 5l7 7h-4v7h-6v-7H5l7-7z" />
                </svg>
              ) : (
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 19l-7-7h4V5h6v7h4l-7 7z" />
                </svg>
              )}
              <span>{formatVariation(variationPercent)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Valor de Mercado</p>
          <p className="mt-2 text-xl font-bold text-gray-900">{formatCurrency(quote.marketCap)}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-xs text-gray-500 uppercase tracking-wide">P/L</p>
          <p className="mt-2 text-xl font-bold text-gray-900">{formatDecimal(quote.priceEarnings)}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-xs text-gray-500 uppercase tracking-wide">LPA (EPS)</p>
          <p className="mt-2 text-xl font-bold text-gray-900">{formatDecimal(quote.earningsPerShare)}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Volume</p>
          <p className="mt-2 text-xl font-bold text-gray-900">{formatInteger(quote.regularMarketVolume)}</p>
          <p className="text-xs text-gray-500 mt-1">{quote.fiftyTwoWeekRange || 'N/D'}</p>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-4 md:p-6 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Histórico da ação ({selectedTicker})</h2>
          <div className="flex flex-wrap gap-2">
            {CHART_RANGE_OPTIONS.map((range) => (
              <button
                key={range}
                type="button"
                onClick={() => setSelectedChartRange(range)}
                className={`px-3 py-1.5 rounded-lg border text-xs font-semibold transition-colors ${
                  selectedChartRange === range
                    ? 'bg-blue-700 border-blue-700 text-white'
                    : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {range}
              </button>
            ))}
          </div>
        </div>

        {isPriceChartLoading ? (
          <div className="flex items-center justify-center py-10">
            <div className="w-10 h-10 border-4 border-gray-200 border-t-blue-700 rounded-full animate-spin"></div>
          </div>
        ) : priceHistory.length > 0 ? (
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={priceHistory}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="dateLabel" tick={{ fill: '#6b7280', fontSize: 12 }} />
                <YAxis
                  tickFormatter={(value) => formatPrice(typeof value === 'number' ? value : Number(value), quote.currency)}
                  tick={{ fill: '#6b7280', fontSize: 12 }}
                />
                <Tooltip
                  formatter={(value) =>
                    formatPrice(typeof value === 'number' ? value : Number(value ?? 0), quote.currency)
                  }
                  labelFormatter={(_, payload) => {
                    const row = payload?.[0]?.payload;
                    const parsedDate = parseChartDate(row?.dateRaw);
                    return parsedDate
                      ? parsedDate.toLocaleString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : 'N/D';
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="fechamento"
                  name="Fechamento"
                  stroke={isPositiveVariation ? '#16a34a' : '#dc2626'}
                  strokeWidth={3}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="text-sm text-gray-600">Sem histórico disponível para o ticker/período selecionado.</p>
        )}
      </div>
    </div>
  );
};

export default CompanyDetailsPage;

